import type { ToolCard, ToolCategoryMeta } from '../types'

/**
 * Seed catalog. Replaced by the live API (`GET /api/ai-tools`) the moment
 * the backend stub returns real data. Keep the shape stable — both the
 * card grid and the eventual detail page rely on these fields.
 *
 * Curated mix of mainstream + niche tools across the 12 categories. The
 * goal is to make the seed convincing enough that the hub looks alive
 * before we wire the database.
 */
export const SEED_TOOLS: ToolCard[] = [
  // Image
  {
    slug: 'midjourney',
    name: { ar: 'Midjourney', en: 'Midjourney' },
    tagline: {
      ar: 'توليد صور سينمائية بجودة فنية عالية، أفضل اختيار للمصممين.',
      en: 'Cinematic AI imagery with the strongest artistic eye on the market.',
    },
    category: 'image',
    pricing: { model: 'paid', startingMonthlyUsd: 10 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: false,
    iconKey: 'sparkles',
    isFeatured: true,
  },
  {
    slug: 'flux',
    name: { ar: 'Flux', en: 'Flux 1' },
    tagline: {
      ar: 'مفتوح المصدر، نتائج photorealistic، يدير وجوه ونصوص أفضل من المنافسين.',
      en: 'Open-weight image model with photorealism and reliable text rendering.',
    },
    category: 'image',
    pricing: { model: 'freemium', startingMonthlyUsd: 0 },
    type: 'web',
    skill: 'intermediate',
    arabicFriendly: false,
    iconKey: 'palette',
    isFeatured: true,
  },
  {
    slug: 'nano-banana',
    name: { ar: 'Nano Banana', en: 'Nano Banana' },
    tagline: {
      ar: 'محرر صور AI سريع جداً، بيعدل صور موجودة بدقة عالية.',
      en: 'Fast AI photo editor that nudges existing images with precision.',
    },
    category: 'image',
    pricing: { model: 'paid', startingMonthlyUsd: 5 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: false,
    iconKey: 'wand-sparkles',
  },
  {
    slug: 'leonardo',
    name: { ar: 'Leonardo AI', en: 'Leonardo AI' },
    tagline: {
      ar: 'منصة شاملة لتوليد وتحرير الصور بنماذج متعددة.',
      en: 'All-in-one image generator with multiple base models and editors.',
    },
    category: 'image',
    pricing: { model: 'freemium', startingMonthlyUsd: 0 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: false,
    iconKey: 'image',
  },

  // Video
  {
    slug: 'sora',
    name: { ar: 'Sora', en: 'Sora' },
    tagline: {
      ar: 'فيديو سينمائي بالـ AI من OpenAI، مدة طويلة ودقة عالية.',
      en: 'OpenAI’s flagship cinematic video model with long-form continuity.',
    },
    category: 'video',
    pricing: { model: 'paid', startingMonthlyUsd: 20 },
    type: 'web',
    skill: 'intermediate',
    arabicFriendly: false,
    iconKey: 'film',
    isFeatured: true,
  },
  {
    slug: 'veo',
    name: { ar: 'Veo 3', en: 'Veo 3' },
    tagline: {
      ar: 'فيديو بـ AI من Google بحركة كاميرا واقعية وصوت متزامن.',
      en: 'Google’s video model with realistic camera moves and synced audio.',
    },
    category: 'video',
    pricing: { model: 'paid', startingMonthlyUsd: 20 },
    type: 'web',
    skill: 'intermediate',
    arabicFriendly: false,
    iconKey: 'clapperboard',
  },
  {
    slug: 'runway',
    name: { ar: 'Runway', en: 'Runway' },
    tagline: {
      ar: 'استوديو فيديو AI كامل: توليد، تعديل، motion graphics.',
      en: 'A full AI video studio — generate, edit, and motion-graphic in one place.',
    },
    category: 'video',
    pricing: { model: 'freemium', startingMonthlyUsd: 12 },
    type: 'web',
    skill: 'intermediate',
    arabicFriendly: false,
    iconKey: 'video',
  },
  {
    slug: 'kling',
    name: { ar: 'Kling', en: 'Kling' },
    tagline: {
      ar: 'فيديو واقعي طويل المدة، أحسن في حركة الإنسان.',
      en: 'Long-form realistic video with strong human motion fidelity.',
    },
    category: 'video',
    pricing: { model: 'freemium', startingMonthlyUsd: 0 },
    type: 'web',
    skill: 'intermediate',
    arabicFriendly: false,
    iconKey: 'play-square',
  },

  // Voice
  {
    slug: 'elevenlabs',
    name: { ar: 'ElevenLabs', en: 'ElevenLabs' },
    tagline: {
      ar: 'استنساخ صوت بشري طبيعي، يدعم العربية بجودة استوديو.',
      en: 'Studio-grade voice cloning and TTS with strong Arabic support.',
    },
    category: 'voice',
    pricing: { model: 'freemium', startingMonthlyUsd: 5 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: true,
    iconKey: 'mic',
  },

  // Writing
  {
    slug: 'chatgpt',
    name: { ar: 'ChatGPT', en: 'ChatGPT' },
    tagline: {
      ar: 'الأكثر شموليّة، يدعم العربي ممتاز ومفيد لكل المهام.',
      en: 'The most general-purpose AI assistant. Strong Arabic. Always relevant.',
    },
    category: 'writing',
    pricing: { model: 'freemium', startingMonthlyUsd: 20 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: true,
    iconKey: 'message-circle',
    isFeatured: true,
  },
  {
    slug: 'claude',
    name: { ar: 'Claude', en: 'Claude' },
    tagline: {
      ar: 'الأقوى في الكتابة الطويلة والتحليل، context طويل جداً.',
      en: 'The deepest analyst with the longest context window. A writer’s pick.',
    },
    category: 'writing',
    pricing: { model: 'freemium', startingMonthlyUsd: 20 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: true,
    iconKey: 'sparkles',
  },
  {
    slug: 'perplexity',
    name: { ar: 'Perplexity', en: 'Perplexity' },
    tagline: {
      ar: 'محرك بحث AI بمصادر، بديل Google للأبحاث.',
      en: 'An AI search engine that cites sources. Replaces Google for research.',
    },
    category: 'writing',
    pricing: { model: 'freemium', startingMonthlyUsd: 20 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: true,
    iconKey: 'search',
  },

  // Code
  {
    slug: 'cursor',
    name: { ar: 'Cursor', en: 'Cursor' },
    tagline: {
      ar: 'محرر كود AI-first مبني على VS Code، سرعة تطوير 10x.',
      en: 'AI-first code editor on top of VS Code. 10x developer velocity.',
    },
    category: 'code',
    pricing: { model: 'freemium', startingMonthlyUsd: 20 },
    type: 'desktop',
    skill: 'intermediate',
    arabicFriendly: false,
    iconKey: 'code',
    isFeatured: true,
  },
  {
    slug: 'github-copilot',
    name: { ar: 'GitHub Copilot', en: 'GitHub Copilot' },
    tagline: {
      ar: 'مساعد البرمجة الأشهر، مدمج مع كل المحررات الكبيرة.',
      en: 'The original AI pair programmer, built into every major editor.',
    },
    category: 'code',
    pricing: { model: 'paid', startingMonthlyUsd: 10 },
    type: 'plugin',
    skill: 'intermediate',
    arabicFriendly: false,
    iconKey: 'github',
  },
  {
    slug: 'v0',
    name: { ar: 'v0', en: 'v0' },
    tagline: {
      ar: 'بناء واجهات React بـ Tailwind من نص — بإتقان مفاجئ.',
      en: 'Generate production-grade React + Tailwind UI from a prompt.',
    },
    category: 'code',
    pricing: { model: 'freemium', startingMonthlyUsd: 20 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: false,
    iconKey: 'sparkles',
  },

  // Music
  {
    slug: 'suno',
    name: { ar: 'Suno', en: 'Suno' },
    tagline: {
      ar: 'توليد أغاني كاملة بكلمات وموسيقى احترافية.',
      en: 'Generate full songs with vocals and instrumentation that actually slap.',
    },
    category: 'music',
    pricing: { model: 'freemium', startingMonthlyUsd: 8 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: false,
    iconKey: 'music',
  },
  {
    slug: 'udio',
    name: { ar: 'Udio', en: 'Udio' },
    tagline: {
      ar: 'منافس قوي لـ Suno، جودة مكس أعلى.',
      en: 'A Suno rival with arguably better mastering and arrangement.',
    },
    category: 'music',
    pricing: { model: 'freemium', startingMonthlyUsd: 10 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: false,
    iconKey: 'music-2',
  },

  // 3D
  {
    slug: 'spline',
    name: { ar: 'Spline', en: 'Spline' },
    tagline: {
      ar: 'تصميم 3D للويب بدون تعقيد، export مباشر لـ React.',
      en: 'Friendly 3D for the web with one-click React export.',
    },
    category: '3d',
    pricing: { model: 'freemium', startingMonthlyUsd: 9 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: false,
    iconKey: 'box',
  },

  // Productivity
  {
    slug: 'notion-ai',
    name: { ar: 'Notion AI', en: 'Notion AI' },
    tagline: {
      ar: 'AI داخل Notion، يكتب ويلخص وينظم محتواك.',
      en: 'AI inside your Notion workspace — writes, summarizes, organizes.',
    },
    category: 'productivity',
    pricing: { model: 'paid', startingMonthlyUsd: 10 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: true,
    iconKey: 'file-text',
  },

  // Design
  {
    slug: 'figma-ai',
    name: { ar: 'Figma AI', en: 'Figma AI' },
    tagline: {
      ar: 'AI مدمج في Figma لإنشاء تصاميم أسرع.',
      en: 'AI baked into Figma — faster ideation and pixel pushing.',
    },
    category: 'design',
    pricing: { model: 'freemium', startingMonthlyUsd: 12 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: false,
    iconKey: 'pen-tool',
  },

  // Marketing
  {
    slug: 'jasper',
    name: { ar: 'Jasper', en: 'Jasper' },
    tagline: {
      ar: 'كتابة محتوى تسويقي، إعلانات، ونشرات بـ AI.',
      en: 'AI marketing copy at scale — ads, emails, posts.',
    },
    category: 'marketing',
    pricing: { model: 'paid', startingMonthlyUsd: 39 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: false,
    iconKey: 'megaphone',
  },

  // Data
  {
    slug: 'julius',
    name: { ar: 'Julius AI', en: 'Julius AI' },
    tagline: {
      ar: 'تحليل بيانات بـ AI، يحوّل أسئلتك لرسوم بيانية.',
      en: 'AI data analyst that turns plain questions into rich charts.',
    },
    category: 'data',
    pricing: { model: 'freemium', startingMonthlyUsd: 20 },
    type: 'web',
    skill: 'intermediate',
    arabicFriendly: false,
    iconKey: 'bar-chart-3',
  },

  // Education
  {
    slug: 'mymemo',
    name: { ar: 'MyMemo', en: 'MyMemo' },
    tagline: {
      ar: 'تذكير ذكي وتحويل ملاحظاتك لكورسات صغيرة.',
      en: 'Spaced-repetition assistant that turns notes into mini-courses.',
    },
    category: 'education',
    pricing: { model: 'freemium', startingMonthlyUsd: 6 },
    type: 'web',
    skill: 'beginner',
    arabicFriendly: false,
    iconKey: 'graduation-cap',
  },
]

export const CATEGORIES: ToolCategoryMeta[] = [
  { slug: 'image', label: { ar: 'الصور', en: 'Image' }, iconKey: 'image', count: 0 },
  { slug: 'video', label: { ar: 'الفيديو', en: 'Video' }, iconKey: 'video', count: 0 },
  { slug: 'voice', label: { ar: 'الصوت', en: 'Voice' }, iconKey: 'mic', count: 0 },
  { slug: 'writing', label: { ar: 'الكتابة', en: 'Writing' }, iconKey: 'pen-tool', count: 0 },
  { slug: 'code', label: { ar: 'البرمجة', en: 'Code' }, iconKey: 'code', count: 0 },
  { slug: 'music', label: { ar: 'الموسيقى', en: 'Music' }, iconKey: 'music', count: 0 },
  { slug: '3d', label: { ar: '3D', en: '3D' }, iconKey: 'box', count: 0 },
  { slug: 'productivity', label: { ar: 'الإنتاجية', en: 'Productivity' }, iconKey: 'check-square', count: 0 },
  { slug: 'design', label: { ar: 'التصميم', en: 'Design' }, iconKey: 'palette', count: 0 },
  { slug: 'marketing', label: { ar: 'التسويق', en: 'Marketing' }, iconKey: 'megaphone', count: 0 },
  { slug: 'data', label: { ar: 'البيانات', en: 'Data' }, iconKey: 'bar-chart-3', count: 0 },
  { slug: 'education', label: { ar: 'التعليم', en: 'Education' }, iconKey: 'graduation-cap', count: 0 },
]

/**
 * Build category counts from the seed list. The API will compute this
 * server-side once we wire it up.
 */
export function buildCategoryMeta(tools: ToolCard[]): ToolCategoryMeta[] {
  const counts = new Map<string, number>()
  for (const t of tools) {
    counts.set(t.category, (counts.get(t.category) ?? 0) + 1)
  }
  return CATEGORIES.map((c) => ({ ...c, count: counts.get(c.slug) ?? 0 }))
}
